import type { Dictionary } from '../../../core';
import type { TranslatableField } from '../../../services/fourwaves/models/TranslatableField';
import type { FormDto } from './FormDto';
import type { ReviewStatus } from './ReviewStatus';
import type { UpdatedByUser } from './UserDto';

export enum SubmissionDecisionEnum {
  Rejected = 'rejected',
  Pending = 'pending',
  Accepted = 'accepted',
}

export interface SubmissionDecisionDto {
  id: string;
  formEntryId: string;
  presentationTypeId: string;
  decision: SubmissionDecisionEnum;
  lastUpdateDate: string | null;
  lastUpdateUser: UpdatedByUser | null;
}

export interface FormEntryValueDto {
  id: string;
  value: string;
  formFieldId: string;
  fieldType: string;
  order?: number;
  creationDate: string;
}

export interface AuthorDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isPresenter: boolean;
  order: number;
  affiliations: string[];
  userId: string | null;
  participantId: string | null;
}

export interface PresentationType {
  id: string;
  name: TranslatableField;
}

export interface PresentationTime {
  sessionId: string;
  start: string;
  end: string;
}

export enum FormEntryStatus {
  Registered = 'Registered',
  Submitted = 'Submitted',
  OnWaitingList = 'OnWaitingList',
  Withdrawn = 'Withdrawn',
  Scheduled = 'Scheduled',
}

export interface FormEntryDto {
  id: string;
  formId: string;
  userId: string;
  number: string;
  formEntryValues: FormEntryValueDto[];
  creationDate: string;
  lastUpdateDate: string | null;
  lastUpdateUser: UpdatedByUser | null;
  paidChoiceIds: string[];
  videoParticipantCount: number;
  listenerCount: number;
  thumbnail?: string;
  isPresentingNow: boolean;
  presentationTimes?: PresentationTime[];
  isDiscussionPermittedBySession: boolean;
  submissionDecisions: SubmissionDecisionDto[];
  isPublished: boolean;
  reviewStatus: ReviewStatus;
  reviewScore: number;
  reviewScoreStdDev: number;
  status: FormEntryStatus;
  withdrawnAt: string | null;
  withdrawnBy: UpdatedByUser | null;
  withdrawalReason: string | null;
  unpaidItems: boolean;
  isMissingRequiredFields: boolean;
  notRegistered: boolean;
  pendingPayment: boolean;
  inConflict: boolean;
  upvoteCount: number;
  isUpvotedByCurrentUser: boolean;
  isBookmarkedByCurrentUser: boolean;
  commentCount: number;
  withSubmission?: boolean;
  participantId?: string;
  affiliation: string;
  authors?: AuthorDto[];
}

export const formEntryStatusColorMap = {
  [FormEntryStatus.Registered]: 'success',
  [FormEntryStatus.Submitted]: 'success',
  [FormEntryStatus.OnWaitingList]: 'gray',
  [FormEntryStatus.Withdrawn]: 'error',
  [FormEntryStatus.Scheduled]: 'primary',
};

export function fillDictionary(formEntry: FormEntryDto, formData: Dictionary<string | string[]>) {
  if (formEntry && formEntry.formEntryValues) {
    for (const formEntryValue of formEntry.formEntryValues) {
      if (!formData[formEntryValue.formFieldId]) {
        formData[formEntryValue.formFieldId] = formEntryValue.value;
      } else if (!Array.isArray(formData[formEntryValue.formFieldId])) {
        formData[formEntryValue.formFieldId] = [formData[formEntryValue.formFieldId] as string];
        (formData[formEntryValue.formFieldId] as string[]).push(formEntryValue.value);
      } else {
        (formData[formEntryValue.formFieldId] as string[]).push(formEntryValue.value);
      }
    }
  }
}

export function getFirstName(formEntry?: FormEntryDto | null, form?: FormDto | null): string {
  if (formEntry == null || form == null) return '';
  const formEntryValue = formEntry.formEntryValues.find(x => x.formFieldId === form.firstNameFormFieldId);
  return formEntryValue ? formEntryValue.value : '';
}

export function getLastName(formEntry?: FormEntryDto | null, form?: FormDto | null): string {
  if (formEntry == null || form == null) return '';
  const formEntryValue = formEntry.formEntryValues.find(x => x.formFieldId === form.lastNameFormFieldId);
  return formEntryValue ? formEntryValue.value : '';
}

export function getEmail(formEntry?: FormEntryDto | null, form?: FormDto | null): string {
  if (formEntry == null || form == null) return '';
  const formEntryValue = formEntry.formEntryValues.find(x => x.formFieldId === form.emailFormFieldId);
  return formEntryValue ? formEntryValue.value : '';
}

export function getSubmissionTitle(formEntry?: FormEntryDto | null, form?: FormDto | null): string {
  if (formEntry == null || form == null) return '';
  const formEntryValue = formEntry.formEntryValues.find(x => x.formFieldId === form.submissionTitleFormFieldId);
  return formEntryValue ? formEntryValue.value : '';
}

export function getAbstract(formEntry?: FormEntryDto | null, form?: FormDto | null): string {
  if (formEntry == null || form == null) return '';
  const formEntryValue = formEntry.formEntryValues.find(x => x.formFieldId === form.submissionAbstractFormFieldId);
  return formEntryValue ? formEntryValue.value : '';
}

export function getFormEntryValue(
  formEntry?: FormEntryDto | null,
  form?: FormDto | null,
  formFieldId?: string,
): string {
  if (formEntry == null || form == null || !formFieldId) return '';
  const formEntryValue = formEntry.formEntryValues.filter(x => x.formFieldId === formFieldId);
  return formEntryValue ? formEntryValue.map(x => x.value).join(',') : '';
}

export function getPresenterUserIds(formEntry: FormEntryDto | null | undefined): string[] {
  return (
    formEntry?.authors
      ?.filter(({ isPresenter }) => isPresenter)
      .map(({ userId }) => userId ?? '')
      .filter(Boolean) ?? []
  );
}
